/**
 * The item library: the corpus, materialised as persisted records (TASKS T46).
 *
 * SPEC §2's module list does not name this file, because in the spec's sketch the boot sequence
 * ("loads store, computes queue") lives in the composition root. It does not belong there. Deciding
 * *which* records exist, seeding them on a first launch, and keeping the in-memory copy in step with
 * what is on disk is a real responsibility with real failure modes, and it needs to be testable
 * without booting a browser. See DECISIONS D-096.
 *
 * What this is **not**: it holds no scheduling policy. It does not decide what is due — `dueQueue`
 * does — and it never filters, caps, or rations. Invariant 7 lives one module down, and nothing here
 * may quietly implement a limit on its behalf.
 */

import { dueQueue, estimatedMinutes, type SecondsPerItem } from '../scheduler/queue';
import type { SeedItem } from '../content/schema';
import { toItemRecord } from '../content/schema';
import { KEY, PREFIX, type ItemRecord } from './schema';
import type { Store } from './store';

export interface LibraryLoadResult {
  library: ItemLibrary;
  /** True when this launch created the records — i.e. the store was empty. */
  seeded: boolean;
}

/**
 * Every corpus item, in memory, backed by the store.
 *
 * The whole corpus is materialised at first launch rather than at intake. Both work, and this one
 * is chosen because `intakeCandidates` selects on `memory.state === 'new'`: an item with no record
 * is invisible to intake, so lazy materialisation would need a second, parallel notion of "exists
 * but not yet a record" and two places that could disagree about what the learner has been offered.
 * Four hundred-odd rows is nothing to IndexedDB.
 */
export class ItemLibrary {
  private readonly byId = new Map<string, ItemRecord>();

  private constructor(
    private readonly store: Store,
    records: readonly ItemRecord[],
  ) {
    for (const record of records) this.byId.set(record.id, record);
  }

  /**
   * Load the library, seeding it from the corpus if the store has no items yet.
   *
   * `now` comes from the injected clock and becomes every new item's `dueAt`, so a fresh install has
   * its whole corpus available to intake immediately. Intake, not the queue, is what rations that
   * (invariant 6) — 430 items do not become 430 reviews on day one.
   */
  static async load(
    store: Store,
    seeds: readonly SeedItem[],
    now: number,
  ): Promise<LibraryLoadResult> {
    const existing = await store.list<ItemRecord>(PREFIX.item);
    if (existing.length > 0) {
      const library = new ItemLibrary(
        store,
        existing.map((entry) => entry.value),
      );
      // A corpus that grew since the last launch adds its new items without touching the old ones:
      // an item's `memory` is the learner's history and is never rebuilt from a file.
      const added = await library.seedMissing(seeds, now);
      return { library, seeded: added > 0 };
    }

    const records = seeds.map((seed) => toItemRecord(seed, now));
    const library = new ItemLibrary(store, records);
    await Promise.all(records.map((record) => store.set(KEY.item(record.id), record)));
    return { library, seeded: true };
  }

  /** Write records for corpus items that have none. Returns how many were added. */
  private async seedMissing(seeds: readonly SeedItem[], now: number): Promise<number> {
    const missing = seeds.filter((seed) => !this.byId.has(seed.id));
    for (const seed of missing) {
      const record = toItemRecord(seed, now);
      this.byId.set(record.id, record);
      await this.store.set(KEY.item(record.id), record);
    }
    return missing.length;
  }

  all(): readonly ItemRecord[] {
    return [...this.byId.values()];
  }

  get(id: string): ItemRecord | undefined {
    return this.byId.get(id);
  }

  size(): number {
    return this.byId.size;
  }

  /** Due reviews, most at-risk first. Never truncated (invariant 7). */
  due(now: number): ItemRecord[] {
    return dueQueue(this.all(), now);
  }

  /** Estimated minutes to work the whole due queue. Drives intake suppression (invariant 6). */
  dueMinutes(now: number, measured: SecondsPerItem = {}): number {
    return estimatedMinutes(this.due(now), measured);
  }

  /**
   * Persist one changed record.
   *
   * Awaited by callers that must not proceed until it is on disk — which, in the run loop, is all of
   * them: the attempt and its updated memory are banked before feedback renders (SPEC §10, DoD-5).
   */
  async put(record: ItemRecord): Promise<void> {
    this.byId.set(record.id, record);
    await this.store.set(KEY.item(record.id), record);
  }
}
